import React, { CSSProperties } from 'react';
import {
  useTranslate,
  useLogout,
  useTitle,
  CanAccess,
  ITreeMenu,
  useIsExistAuthentication,
  useRouterContext,
  useMenu,
  useRefineContext,
  useLink,
  useRouterType,
  useActiveAuthProvider,
  pickNotDeprecated,
  useWarnAboutChange,
  useGetLocale,
} from '@refinedev/core';
import { useThemedLayoutContext } from '@refinedev/antd';
import {
  DashboardOutlined,
  LogoutOutlined,
  UnorderedListOutlined,
  BarsOutlined,
  LeftOutlined,
  RightOutlined,
  CaretRightOutlined,
} from '@ant-design/icons';
import { Layout, Menu, Grid, Drawer, Button, theme } from 'antd';
import type { RefineThemedLayoutV2SiderProps } from '@refinedev/antd';
import { CcThemedTitleV2 } from './title';
import { colors } from '../../styles/colors';
import useStyles from './sider.style';

const drawerButtonStyles: CSSProperties = {
  borderTopLeftRadius: 0,
  borderBottomLeftRadius: 0,
  position: 'fixed',
  top: 64,
  zIndex: 999,
};

export const CcThemedSiderV2: React.FC<RefineThemedLayoutV2SiderProps> = ({
  Title: TitleFromProps,
  render,
  meta,
  fixed,
  activeItemDisabled = false,
}) => {
  const { token } = theme.useToken();
  const {
    siderCollapsed,
    setSiderCollapsed,
    mobileSiderOpen,
    setMobileSiderOpen,
  } = useThemedLayoutContext();

  const { styles } = useStyles();

  const locale = useGetLocale();

  const isExistAuthentication = useIsExistAuthentication();
  const routerType = useRouterType();
  const NewLink = useLink();
  const { warnWhen, setWarnWhen } = useWarnAboutChange();
  const { Link: LegacyLink } = useRouterContext();
  const Link = routerType === 'legacy' ? LegacyLink : NewLink;
  const TitleFromContext = useTitle();
  const translate = useTranslate();
  const { menuItems, selectedKey, defaultOpenKeys } = useMenu({ meta });
  const breakpoint = Grid.useBreakpoint();
  const { hasDashboard } = useRefineContext();
  const authProvider = useActiveAuthProvider();
  const { mutate: mutateLogout } = useLogout({
    v3LegacyAuthProviderCompatible: Boolean(authProvider?.isLegacy),
  });

  const isMobile =
    typeof breakpoint.lg === 'undefined' ? false : !breakpoint.lg;

  const RenderToTitle = TitleFromProps ?? TitleFromContext ?? CcThemedTitleV2;

  const renderTreeView = (tree: ITreeMenu[], selectedKey?: string) => {
    return tree.map((item: ITreeMenu) => {
      const {
        icon,
        label,
        route,
        key,
        name,
        children,
        parentName,
        meta,
        options,
      } = item;

      if (children.length > 0) {
        return (
          <CanAccess
            key={item.key}
            resource={name.toLowerCase()}
            action="list"
            params={{
              resource: item,
            }}
          >
            <Menu.SubMenu
              key={item.key}
              icon={icon ?? <UnorderedListOutlined />}
              title={label}
            >
              {renderTreeView(children, selectedKey)}
            </Menu.SubMenu>
          </CanAccess>
        );
      }
      const isSelected = key === selectedKey;
      const isRoute = !(
        pickNotDeprecated(meta?.parent, options?.parent, parentName) !==
          undefined && children.length === 0
      );

      const baseStyle: React.CSSProperties = {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      };

      const linkStyle: React.CSSProperties =
        activeItemDisabled && isSelected
          ? { ...baseStyle, pointerEvents: 'none' }
          : baseStyle;

      return (
        <CanAccess
          key={item.key}
          resource={name.toLowerCase()}
          action="list"
          params={{
            resource: item,
          }}
        >
          <Menu.Item
            key={item.key}
            icon={icon ?? (isRoute && <UnorderedListOutlined />)}
            style={linkStyle}
            className={styles['ccSiderMenuItem']}
          >
            <Link to={route ?? ''} style={linkStyle}>
              {label}
            </Link>
            {!siderCollapsed && isSelected && (
              <CaretRightOutlined className={styles.selectedArrow} />
            )}
          </Menu.Item>
        </CanAccess>
      );
    });
  };

  const handleLogout = () => {
    if (warnWhen) {
      const confirm = window.confirm(translate('warnWhenUnsavedChanges'));

      if (confirm) {
        setWarnWhen(false);
        mutateLogout();
      }
    } else {
      mutateLogout();
    }
  };

  const logout = isExistAuthentication && (
    <Menu.Item
      key="logout"
      onClick={() => handleLogout()}
      icon={<LogoutOutlined />}
    >
      {translate('buttons.logout', 'Logout')}
    </Menu.Item>
  );

  const dashboard = hasDashboard ? (
    <Menu.Item key="dashboard" icon={<DashboardOutlined />}>
      <Link to="/">{translate('dashboard.title', 'Dashboard')}</Link>
      {!siderCollapsed && selectedKey === '/' && (
        <CaretRightOutlined className={styles.selectedArrow} />
      )}
    </Menu.Item>
  ) : null;

  const items = renderTreeView(menuItems, selectedKey);

  const expandedWidth = locale()?.startsWith('ru') ? 250 : 235;

  const renderSider = () => {
    if (render) {
      return render({
        dashboard,
        items,
        logout,
        collapsed: siderCollapsed,
      });
    }
    return (
      <>
        {dashboard}
        {items}
        {logout}
      </>
    );
  };

  const renderMenu = (collapsed = false) => {
    return (
      <Menu
        selectedKeys={selectedKey ? [selectedKey] : []}
        defaultOpenKeys={collapsed ? undefined : defaultOpenKeys}
        mode="inline"
        inlineCollapsed={collapsed}
        style={{
          paddingTop: '8px',
          border: 'none',
          overflow: 'auto',
          height: 'calc(100% - 62px)',
          width: collapsed ? 80 : expandedWidth,
        }}
        onClick={() => {
          setMobileSiderOpen(false);
        }}
      >
        {renderSider()}
      </Menu>
    );
  };

  const renderDrawerSider = () => {
    return (
      <div data-testid="cc-sider-mobile">
        <Drawer
          open={mobileSiderOpen}
          onClose={() => setMobileSiderOpen(false)}
          placement="left"
          closable={false}
          width={expandedWidth}
          styles={{
            body: {
              padding: 0,
              // @ts-ignore
              backgroundColor: token.Layout.siderBg,
            },
          }}
          maskClosable={true}
        >
          <Layout>
            <Layout.Sider
              width={expandedWidth}
              style={{
                // height: '100vh',
                // @ts-ignore
                backgroundColor: token.Layout.siderBg,
                borderRight: `1px solid ${colors['--triad-blue-4']}`,
              }}
            >
              <div
                style={{
                  width: expandedWidth,
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  height: '64px',
                  backgroundColor: colors['--triad-blue-4'],
                }}
              >
                <RenderToTitle collapsed={false} />
              </div>
              {renderMenu()}
            </Layout.Sider>
          </Layout>
        </Drawer>
        <Button
          style={drawerButtonStyles}
          size="large"
          onClick={() => setMobileSiderOpen(true)}
          icon={<BarsOutlined />}
          // the narrow-layout menu button lives outside the Sider, so the print CSS's sider rule misses it: on an A4
          // page (narrower than the breakpoint) it printed over the worksheet's title
          data-print-hide
        ></Button>
      </div>
    );
  };

  const siderStyles: React.CSSProperties = {
    // @ts-ignore
    backgroundColor: token.Layout.siderBg,
    borderRight: `1px solid ${colors['--triad-blue-4']}`,
    // height: '100vh',
  };

  if (fixed) {
    siderStyles.position = 'fixed';
    siderStyles.top = 0;
    // siderStyles.height = '100vh';
    siderStyles.zIndex = 999;
  }

  return isMobile ? (
    renderDrawerSider()
  ) : (
    <div
      data-testid="cc-sider"
      style={{
        backgroundColor: token?.Layout?.siderBg || 'transparent',
      }}
    >
      {fixed && (
        <div
          style={{
            width: siderCollapsed ? 80 : expandedWidth,
            transition: 'all 0.2s',
          }}
        />
      )}
      <Layout.Sider
        style={siderStyles}
        width={expandedWidth}
        collapsible
        collapsed={siderCollapsed}
        onCollapse={(collapsed, type) => {
          if (type === 'clickTrigger') {
            setSiderCollapsed(collapsed);
          }
        }}
        collapsedWidth={80}
        breakpoint="lg"
        trigger={
          <Button
            type="text"
            style={{
              borderRadius: 0,
              height: '100%',
              width: '100%',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
            }}
          >
            {siderCollapsed ? (
              <RightOutlined
                data-testid="cc-sider-expand-icon"
                style={{
                  color: colors['--white'],
                }}
              />
            ) : (
              <LeftOutlined
                data-testid="cc-sider-collapse-icon"
                style={{
                  color: colors['--white'],
                }}
              />
            )}
          </Button>
        }
      >
        <div
          style={{
            width: siderCollapsed ? 80 : expandedWidth,
            display: 'flex',
            justifyContent: siderCollapsed ? 'center' : 'flex-start',
            alignItems: 'center',
            height: '64px',
            backgroundColor: colors['--triad-blue-4'],
            fontSize: '14px',
          }}
        >
          <RenderToTitle collapsed={siderCollapsed} />
        </div>
        {renderMenu(siderCollapsed)}
      </Layout.Sider>
    </div>
  );
};
